import { LogIn, LogOut, ShieldOff, UserRound } from "lucide-react";
import { useState } from "react";
import {
  LOGOUT_PATH,
  readCsrfToken,
  signInHref,
  useSession,
} from "./useSession";

/**
 * Who you are, in the app shell.
 *
 * Sign-in is a real anchor because it must be: OAuth is a full-page redirect, and `fetch`
 * cannot perform one. Sign-out is a `<button>` because it changes state — it is a POST
 * carrying the CSRF token, which is exactly the distinction between the two elements.
 *
 * When authentication is unconfigured this renders no sign-in control at all. Offering a
 * button that is guaranteed to answer 503 teaches the user that the app is broken rather
 * than that the environment is deliberately without it.
 */

const initialsOf = (userId: string): string =>
  userId
    .replace(/^user[-_]/i, "")
    .slice(0, 2)
    .toUpperCase() || "??";

export const AccountMenu = ({ path }: { path: string }) => {
  const { state, reload } = useSession();
  const [signingOut, setSigningOut] = useState(false);
  const [signOutError, setSignOutError] = useState<string | null>(null);

  const signOut = async (): Promise<void> => {
    const token = readCsrfToken();
    if (token === null) {
      setSignOutError("Your session is missing its request token.");
      return;
    }
    setSigningOut(true);
    setSignOutError(null);
    try {
      const response = await fetch(LOGOUT_PATH, {
        method: "POST",
        headers: { "x-lc-csrf": token, Accept: "application/json" },
      });
      if (!response.ok) {
        setSignOutError("Sign-out was refused.");
        return;
      }
      reload();
    } catch {
      setSignOutError("Sign-out could not be sent.");
    } finally {
      setSigningOut(false);
    }
  };

  if (state.status === "loading") {
    return (
      <span className="account-menu account-menu--quiet" role="status">
        Checking sign-in…
      </span>
    );
  }

  if (state.status === "unconfigured") {
    return (
      <span className="account-menu account-menu--quiet">
        <ShieldOff aria-hidden size={18} />
        <span>
          <strong>Sign-in unavailable</strong>
          <small>Not configured in this environment</small>
        </span>
      </span>
    );
  }

  if (state.status === "error") {
    return (
      <span className="account-menu account-menu--quiet" role="status">
        <ShieldOff aria-hidden size={18} />
        <span>
          <strong>Sign-in state unknown</strong>
          <small>The server could not be reached</small>
        </span>
      </span>
    );
  }

  if (state.status === "anonymous") {
    return (
      <a className="account-menu account-menu--signin" href={signInHref(path)}>
        <LogIn aria-hidden size={18} />
        <span>
          <strong>Sign in</strong>
          <small>Google · invited members only</small>
        </span>
      </a>
    );
  }

  const { session } = state;
  return (
    <span className="account-menu">
      <span className="owner-avatar" aria-hidden>
        {initialsOf(session.leagueCommandUserId)}
      </span>
      <span>
        <strong>{session.leagueCommandUserId}</strong>
        <small>
          <UserRound aria-hidden size={11} /> Signed in
        </small>
      </span>
      <button
        className="lc-button lc-button--secondary"
        type="button"
        disabled={signingOut}
        onClick={() => void signOut()}
      >
        <LogOut aria-hidden size={15} />
        Sign out
      </button>
      {signOutError === null ? null : <span role="alert">{signOutError}</span>}
    </span>
  );
};
